import {
	trackSportsbookAddToBetslip,
	trackSportsbookCheckBetFailed,
	trackSportsbookNewOfferShown,
	trackSportsbookNewOfferAccepted,
	trackSportsbookNewOfferRejected,
	trackSportsbookPlaceBetAttempt,
	trackSportsbookPlaceBetFailed,
	trackSportsbookPlaceBetSuccessful
} from '@comeon/analytics';
import { Middleware, MiddlewareAPI } from 'redux';
import { RootState, AppDispatch, AppAction } from '../../../../../../store/types';
import {
	BetsPlacementStartedAction,
	BETS_PLACEMENT_STARTED,
	BetsCheckedAction,
	BETS_CHECKED,
	BETS_PLACEMENT_UPDATE_RECEIVED,
	BetsPlacementUpdateReceivedAction,
	NEW_OFFER_ACCEPTED,
	NEW_OFFER_REJECTED,
	NewOfferAcceptedAction,
	NewOfferRejectedAction
} from './betslip-betting-duck';
import { ItemsAddedAction, ITEMS_ADDED } from './betslip-data-duck';
import { selectBets } from '.';
import { bettingApiErrorToBetslipBetError } from './betslip-duck-utils';
import { PlaceBetsResponseBetStatus } from '../../../api/betting-service/betting-service-types';
import { BetPlacementStatus } from '../sportsbook-betslip-types';

// Specify actions we use for analytics
type Actions =
	| ItemsAddedAction
	| BetsPlacementStartedAction
	| BetsCheckedAction
	| BetsPlacementUpdateReceivedAction
	| NewOfferAcceptedAction
	| NewOfferRejectedAction;

const trackEventFromAction = (store: MiddlewareAPI<AppDispatch, RootState>, action: AppAction<Actions>) => {
	if (!action || !action.type) {
		return;
	}

	switch (action.type) {
		case ITEMS_ADDED: {
			action.payload.items.forEach(item => {
				trackSportsbookAddToBetslip(item.eventId, item.marketId, item.selectionId, item.odds || 0, item?.tag);
			});
			break;
		}
		case BETS_CHECKED: {
			const bets = selectBets(store.getState());
			action.payload.bets.forEach(checkedBet => {
				const bet = bets.find(b => b.ref === checkedBet.ref);
				// Jump out if no bet or no errors
				if (!bet || checkedBet.errors.length === 0) {
					return;
				}
				// Find all new errors, filter out errors that were already present, and filter out duplicates
				const errors = checkedBet.errors
					.map(bettingApiErrorToBetslipBetError)
					.map(error => error.type)
					.filter(error => !bet.errors.some(betError => betError.type === error))
					.filter((value, index, self) => self.indexOf(value) === index);
				// Track all new errors
				errors.forEach(error => {
					trackSportsbookCheckBetFailed(bet.type, error);
				});
			});
			break;
		}
		case BETS_PLACEMENT_STARTED: {
			const { betRefs, betType, totalStake } = action.payload;
			trackSportsbookPlaceBetAttempt(betRefs.length, betType, totalStake);
			break;
		}
		case BETS_PLACEMENT_UPDATE_RECEIVED: {
			const bets = selectBets(store.getState());
			action.payload.bets.forEach(placedBet => {
				const bet = bets.find(b => b.ref === placedBet.ref);
				// Jump out if no bet or we are not actively placing the bet

				const tagsForBet = bet?.items.map(item => item?.tag).filter(tag => !!tag);

				if (!bet || bet.placementStatus !== BetPlacementStatus.PLACING) {
					return;
				}
				// Bet placed successfully
				if (placedBet.status === PlaceBetsResponseBetStatus.PLACED) {
					if (tagsForBet) {
						trackSportsbookPlaceBetSuccessful(bet.type, bet.stake, tagsForBet.toString());
						return;
					}

					trackSportsbookPlaceBetSuccessful(bet.type, bet.stake);

					return;
				}
				// Bet received new offer
				if (placedBet.newOffer) {
					trackSportsbookNewOfferShown();
					return;
				}
				// Bet failed to place
				if (placedBet.status === PlaceBetsResponseBetStatus.ERROR) {
					const errors = placedBet.errors
						.map(bettingApiErrorToBetslipBetError)
						.map(error => error.type)
						.filter((value, index, self) => self.indexOf(value) === index);
					errors.forEach(error => {
						if (tagsForBet) {
							trackSportsbookPlaceBetFailed(bet.type, error, bet.stake, tagsForBet.toString());
							return;
						}
						trackSportsbookPlaceBetFailed(bet.type, error, bet.stake);
					});
				}
			});
			break;
		}
		case NEW_OFFER_ACCEPTED: {
			trackSportsbookNewOfferAccepted();
			break;
		}
		case NEW_OFFER_REJECTED: {
			trackSportsbookNewOfferRejected();
			break;
		}
		default:
			break;
	}
};

const createBetslipAnalyticsMiddleware: () => Middleware<unknown, RootState, AppDispatch> =
	// @ts-expect-error - probably should't assume that action is known, doing so would be lying to Typescript, consider adding type guard to narrow down action type https://github.com/reduxjs/redux/issues/4518
	() => store => next => (action: AppAction<Actions>) => {
		try {
			// Track the event
			trackEventFromAction(store, action);

			// Dispatch the action
			const result = next(action);

			// Continue
			return result;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Caught an exception!', error);
			throw error;
		}
	};

export default createBetslipAnalyticsMiddleware;
